<template>
  <div class="ap-card prof-card">
    <div class="photo-panel">
      <img
        :src="doctor.photo"
        style="width: 100%; height: 100%; object-fit: cover; position: absolute; inset: 0;"
        alt="Doctor"
        class="prof-photo-img"
      />
    </div>

    <div class="doc-meta">
      <div class="meta-top mb-2">
        <h2 class="doc-name">{{ doctor.name }}</h2>
        <div v-if="doctor.short_description" class="doc-tagline">
          {{ doctor.short_description }}
        </div>
        <div v-if="doctor.department" class="dept-tag mt-2">
          <i class="fas fa-stethoscope me-1"></i>{{ doctor.department }}
        </div>
      </div>

      <div v-if="doctor.full_description" class="doc-bio-section mb-3">
        <p class="bio-label">About</p>
        <div
          class="doc-full-desc"
          :class="{ 'expanded': isBioExpanded }"
          v-html="doctor.full_description"
        ></div>
        <button
          type="button"
          v-if="doctor.full_description.length > 180"
          class="btn-read-more"
          @click="isBioExpanded = !isBioExpanded"
        >
          {{ isBioExpanded ? 'Read Less' : 'Read More' }}
        </button>
      </div>

      <div class="fee-row mt-auto">
        <div v-if="doctor.sales_price">
          <div class="fee-lbl">Consultation Fee</div>
          <div class="fee-val">৳ {{ doctor.sales_price }}</div>
        </div>
        <div class="vr-sep"></div>
        <div v-if="doctor.service_duration">
          <div class="fee-lbl">Duration</div>
          <div class="fee-dur">{{ doctor.service_duration }} min</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  doctor: {
    type: Object,
    required: true,
  },
  hospitalName: {
    type: String,
    default: '',
  },
});

const isBioExpanded = ref(false);
</script>

<style scoped>
.ap-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 16px rgba(115, 103, 240, 0.1);
  padding: 18px 20px;
}

.prof-card {
  padding: 0 !important;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 400px;
}

.photo-panel {
  width: 42%;
  flex-shrink: 0;
  background: linear-gradient(160deg, #c9c1f8 0%, #9186f3 60%, #7367f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 400px;
}

.dept-tag {
  display: inline-flex;
  align-items: center;
  background: #ede9fd;
  color: #7367f0;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.doc-meta {
  flex: 1;
  padding: 24px 22px;
  display: flex;
  flex-direction: column;
}

.doc-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d2d3a;
  margin: 0 0 4px;
  line-height: 1.2;
}

.doc-tagline {
  font-size: 0.92rem;
  color: #7367f0;
  font-weight: 600;
  line-height: 1.4;
}

.doc-bio-section {
  position: relative;
}

.bio-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}

.doc-full-desc {
  font-size: 0.88rem;
  color: #4b4b5a;
  line-height: 1.6;
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: all 0.3s ease;
}

.doc-full-desc.expanded {
  -webkit-line-clamp: initial;
  display: block;
  overflow: visible;
}

.btn-read-more {
  background: none;
  border: none;
  color: #7367f0;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0;
  margin-top: 4px;
  cursor: pointer;
  text-decoration: underline;
}

.doc-full-desc :deep(p) {
  margin-bottom: 0.8rem;
}

.doc-full-desc :deep(ul) {
  padding-left: 1.2rem;
  margin-bottom: 0.8rem;
}

.doc-full-desc :deep(li) {
  margin-bottom: 0.3rem;
}

.ap-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 10px;
}

.b-purple {
  background: #ede9fd;
  color: #7367f0;
}

.b-amber {
  background: #fff3e0;
  color: #b45309;
}

.b-green {
  background: #f0fdf4;
  color: #166534;
}

.fee-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  background: #ede9fd;
  border-radius: 6px;
  border: 1px solid rgba(115, 103, 240, 0.2);
}

.fee-lbl {
  color: #6e6b7b;
  font-size: 0.7rem;
}

.fee-val {
  color: #7367f0;
  font-size: 0.95rem;
  font-weight: 700;
}

.fee-dur {
  color: #2d2d3a;
  font-size: 0.82rem;
  font-weight: 600;
}

.vr-sep {
  width: 1px;
  height: 26px;
  background: rgba(115, 103, 240, 0.2);
}

@media (max-width: 576px) {
  .prof-card {
    flex-direction: column;
  }
  .photo-panel {
    width: 100%;
  }
}
</style>
